import React, { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { FiArrowRight } from "react-icons/fi";

gsap.registerPlugin(ScrollTrigger);

const AboutSection = () => {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const underlineRef = useRef(null);
  const textRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion) {
      return;
    }

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: "top 70%",
        toggleActions: "play none none none",
      },
    });

    tl.from(headingRef.current, {
      opacity: 0,
      y: 50,
      duration: 0.8,
      ease: "power3.out",
    })
      .from(
        underlineRef.current,
        {
          scaleX: 0,
          transformOrigin: "left center",
          duration: 0.8,
          ease: "power3.out",
        },
        "-=0.5"
      )
      .from(
        textRef.current.children,
        {
          opacity: 0,
          y: 20,
          duration: 0.6,
          stagger: 0.2,
          ease: "power2.out",
        },
        "-=0.4"
      )
      .from(
        imageRef.current,
        {
          opacity: 0,
          x: 100,
          duration: 1,
          ease: "power3.out",
        },
        "<"
      );

    return () => {
      tl.kill();
      ScrollTrigger.getAll().forEach((t) => t.kill());
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-stone-100/50 py-16 md:py-24"
    >
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-stone-50 via-white to-stone-100"></div>
      <div className="absolute left-0 top-0 h-32 w-32 -translate-x-10 -translate-y-10 rounded-full bg-amber-100/30 opacity-50 blur-2xl filter"></div>
      <div className="absolute bottom-0 right-0 h-48 w-48 translate-x-16 translate-y-16 rounded-full bg-nature-green-light/10 opacity-60 blur-3xl filter"></div>

      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 md:grid-cols-2 lg:gap-20">
        {/* Text Content */}
        <div className="text-left">
          <div className="inline-block">
            <h2
              ref={headingRef}
              className="font-serif text-3xl font-semibold tracking-wide text-nature-green-dark md:text-4xl"
            >
              Rooted in Unity, Growing with Dignity
            </h2>
            <div ref={underlineRef} className="mt-2 h-1 bg-amber-400"></div>
          </div>
          <div ref={textRef} className="mt-6 space-y-4">
            <p className="text-base leading-relaxed text-gray-700 md:text-lg">
              Nunya is a tapestry woven from threads of shared heritage,
              resilience, and a collective vision for a brighter future. We
              honor our elders, empower our youth, and welcome all who share our
              commitment to progress and mutual respect.
            </p>
            <p className="text-base leading-relaxed text-gray-700 md:text-lg">
              From vibrant local markets to quiet, reflective groves, Nunya is a
              place where tradition and modernity walk hand-in-hand. Located in
              Isuikwuato LGA, Abia State, Nigeria, our village is known for its
              unity, hospitality, and rich culture.
            </p>
            <a
              href="/about"
              className="group mt-4 inline-flex items-center font-semibold text-amber-600 transition-colors duration-300 hover:text-amber-700"
            >
              Learn More About Our Story
              <FiArrowRight className="ml-2 transform transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </div>
        </div>

        {/* Image Content */}
        <div ref={imageRef} className="relative">
          <div className="aspect-w-4 aspect-h-3 overflow-hidden rounded-2xl border border-amber-200/30 shadow-lg">
            <img
              src={require("assets/img/places/place1.png")}
              alt="A vibrant community gathering"
              className="h-full w-full transform object-cover transition-transform duration-500 ease-in-out hover:scale-105"
            />
          </div>
          <div className="absolute -bottom-4 -right-4 flex h-24 w-24 items-center justify-center rounded-full bg-white/80 shadow-xl backdrop-blur-sm">
            <span className="text-4xl">🤝</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
